import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { FiChevronRight, FiHome } from 'react-icons/fi';
import { useBreadcrumbState } from 'context/BreadcrumbContext';
import { getAutoTrail } from './breadcrumbHelpers';

// Link có hiệu ứng gạch chân chạy từ trái -> phải khi hover (Tailwind, prefix tw-).
// tw-no-underline + hover:tw-no-underline: bỏ gạch chân của <a> ở CẢ trạng thái hover.
// (Theme đặt $link-hover-decoration: underline => Bootstrap sinh `a:hover{underline}`
//  với specificity (0,1,1), nên phải dùng hover:tw-no-underline (0,2,0) mới thắng,
//  nếu không sẽ bị "double line": gạch gốc + đường ::after.)
// after:tw-bg-current: đường gạch dùng currentColor => màu line LUÔN khớp màu chữ,
// nên khi hover đổi sang indigo-600 thì cả chữ lẫn line cùng đổi màu.
const linkClass =
  'tw-relative tw-no-underline hover:tw-no-underline tw-text-slate-500 hover:tw-text-primary-600 tw-transition-colors ' +
  "after:tw-content-[''] after:tw-absolute after:tw-left-0 after:-tw-bottom-0.5 " +
  'after:tw-h-[2px] after:tw-w-0 after:tw-bg-current ' +
  'after:tw-transition-[width] after:tw-duration-300 hover:after:tw-w-full';

// Crumb cuối chỉ nhấn nhẹ — title thật của trang thuộc về PageHeader,
// breadcrumb chỉ là đường dẫn định vị.
const activeClass = 'tw-font-semibold tw-text-slate-700';

const Separator = () => (
  <FiChevronRight
    className="tw-mx-1.5 tw-text-slate-300 tw-flex-shrink-0"
    size={16}
    aria-hidden
  />
);

/**
 * PebBreadcrumb — breadcrumb động dùng chung ở header.
 *
 * Thứ tự ưu tiên nguồn dữ liệu:
 *   1. prop `items` (truyền trực tiếp)
 *   2. context (page set qua hook `useBreadcrumb`)
 *   3. tự suy ra từ URL (getAutoTrail) — phủ mọi page mặc định
 *
 * Item: { label, to?, icon?, active? }. Item cuối / active => text, không link.
 * Component luôn tự thêm "Home" ở đầu.
 */
const PebBreadcrumb = ({ className, items: itemsProp }) => {
  const location = useLocation();
  const { items: itemsCtx } = useBreadcrumbState();

  const items = itemsProp || itemsCtx || getAutoTrail(location.pathname);

  return (
    <nav aria-label="breadcrumb" className={classNames('tw-text-xs', className)}>
      <ol className="tw-flex tw-items-center tw-flex-wrap tw-gap-y-1 tw-m-0 tw-p-0 tw-list-none">
        {/* Home luôn đứng đầu */}
        <li className="tw-flex tw-items-center">
          <Link to="/" className={classNames('tw-flex tw-items-center', linkClass)}>
            <FiHome className="tw-mr-1" aria-hidden />
            <span>Home</span>
          </Link>
        </li>

        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isActive = item.active ?? isLast;
          const key = item.to || `${item.label}-${index}`;
          // title đầy đủ khi hover (chỉ khi label là chuỗi).
          const titleAttr =
            typeof item.label === 'string' ? item.label : undefined;

          return (
            <li key={key} className="tw-flex tw-items-center tw-min-w-0">
              <Separator />
              {isActive || !item.to ? (
                <span
                  className={classNames('tw-flex tw-items-center tw-min-w-0', activeClass)}
                  aria-current="page"
                >
                  {item.icon && (
                    <span className="tw-mr-1 tw-text-slate-900">{item.icon}</span>
                  )}
                  <span
                    className="tw-truncate tw-max-w-[240px]"
                    title={titleAttr}
                  >
                    {item.label}
                  </span>
                </span>
              ) : (
                <Link
                  to={item.to}
                  className={classNames('tw-flex tw-items-center tw-min-w-0', linkClass)}
                >
                  {item.icon && <span className="tw-mr-1">{item.icon}</span>}
                  <span className="tw-truncate tw-max-w-[160px]" title={titleAttr}>
                    {item.label}
                  </span>
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

PebBreadcrumb.propTypes = {
  className: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      to: PropTypes.string,
      icon: PropTypes.node,
      active: PropTypes.bool
    })
  )
};

export default PebBreadcrumb;
